import { api } from "@/utils/api";
import { loginUser } from "@/features/user/userSlice";
import { toast } from "@/utils/appToast";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { useTranslation } from 'react-i18next';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { createMetaConversionPayload, fireMetaPixelEvent } from "@/utils/metaPixel";
import GoogleSignUp from '@/utils/googleSignup'

function RegisterWorkerForm({ handlePopupType, onSuccess }) {
    const { t } = useTranslation();
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [showPassword, setShowPassword] = useState({
        password: false,
        repeatPassword: false,
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const registerForm = e.target as HTMLFormElement;
        const registerData = new FormData(registerForm);

        if (!phone || phone.replace(/\D/g, '').length < 11) {
            toast.error(t("toastValidPhone"));
            setPhoneError(t("toastValidPhone"));
            return;
        }

        const email = registerData.get("email") as string;
        if (!email || !validateEmail(email)) {
            toast.error(t("toastValidEmail"));
            return;
        }

        if (registerData.get("password") !== registerData.get("repeatPassword")) {
            toast.error(t("PasswordDontMatch"));
            return;
        }

        const password = registerData.get("password") as string;
        const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{6,}$/;
        if (!strongPasswordRegex.test(password)) {
            toast.error(t("toastPasswordStrongRequirements"));
            return;
        }

        const newUser = {
            first_name: registerData.get("first_name") as string,
            last_name: registerData.get("last_name") as string,
            email: registerData.get("email") as string,
            password: registerData.get("password") as string,
            phone: '+' + phone,
            type: "worker",
        };

        setIsLoading(true);
        try {
            const meta = createMetaConversionPayload();
            const { status, data } = await api.signUp({
                ...newUser,
                ...meta,
            });
            setIsLoading(false);

            if (status === "error") return toast.error(data.message);

            fireMetaPixelEvent("CompleteRegistration", meta.eventId);

            const { token, user } = data;
            dispatch(loginUser({ token, user }));
            if (onSuccess) {
                onSuccess();
            }
            toast.success("User created successfully. Thank you for registering.");

            registerForm.reset();
            setPhone('');
            setPhoneError('');
            handlePopupType('Close');

            navigate("/update-worker");
        } catch (error) {
            setIsLoading(false);
            toast.error(t("toastRegisterUserError"));
        }
    };

    const fields = [
        {
            id: "registerUserName",
            type: "text",
            name: "first_name",
            placeholder: "Nombre",
            label: "Nombre",
        },
        {
            id: "registerUserLastname",
            type: "text",
            name: "last_name",
            placeholder: "Apellido",
            label: "Apellido",
        },
        {
            id: "registerUserEmail",
            type: "text",
            name: "email",
            placeholder: "Email",
            label: "Email",
        },
        {
            id: "registerUserPhone",
            type: "number",
            name: "phone",
            isPhone: true,
            placeholder: "Teléfono",
            label: "Teléfono",
        },
        {
            id: "registerUserPassword",
            type: "password",
            name: "password",
            placeholder: "Contraseña",
            label: "Contraseña",
        },
        {
            id: "registerUserRepeatPassword",
            type: "password",
            name: "repeatPassword",
            placeholder: "Repetir Contraseña",
            label: "Repetir Contraseña",
        },
    ];

    return (
        <>
            <form onSubmit={handleRegister}>
                <div className="form-group">
                    <div className="row mb-5">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="registerUserName" style={{ color: "white" }}>
                                    {t('firstName')} <span style={{ color: "red" }}> *</span>
                                </label>
                                <input
                                    id="registerUserName"
                                    name="first_name"
                                    type="text"
                                    placeholder={t('firstName')}
                                    style={{ borderRadius: 0 }}
                                    required
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="registerUserLastname" style={{ color: "white" }}>
                                    {t('lastName')} <span style={{ color: "red" }}> *</span>
                                </label>
                                <input
                                    id="registerUserLastname"
                                    name="last_name"
                                    type="text"
                                    placeholder={t('lastName')}
                                    style={{ borderRadius: 0 }}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="registerUserPhone" style={{ color: "white" }}>
                            {t('phone')} <span style={{ color: "red" }}> *</span>
                        </label>
                        <PhoneInput
                            country={'us'}
                            countryCodeEditable={false}
                            value={phone}
                            onChange={value => {
                                setPhone(value);
                                setPhoneError('');
                            }}
                            inputProps={{
                                name: 'phone',
                                required: true,
                                id: 'registerUserPhone',
                                style: { flex: 6, borderRadius: 0, width: '100%', minHeight: '60px', fontSize: '14px' }
                            }}
                            inputStyle={{
                                width: '100%',
                                borderRadius: 0,
                                minHeight: '60px',
                                fontSize: '14px'
                            }}
                            buttonStyle={{
                                borderRadius: 0
                            }}
                            containerStyle={{
                                width: '100%'
                            }}
                            specialLabel=""
                            disableDropdown={false}
                            onlyCountries={['us']}
                            onBlur={() => {
                                if (phone && phone.replace(/\D/g, '').length < 11) setPhoneError(t("toastValidPhone"));
                            }}
                        />
                        {phoneError && <div style={{ color: 'red', fontSize: 12 }}>{phoneError}</div>}
                    </div>

                    {fields.slice(2).map((field) => {
                        if (field.isPhone) return null;

                        const isPasswordField =
                            field.name === "password" || field.name === "repeatPassword";

                        return (
                            <div key={field.id} className="form-group">
                                <label htmlFor={field.id} style={{ color: "white" }}>
                                    {t(field.name)} <span style={{ color: "red" }}> *</span>
                                </label>

                                {!isPasswordField ? (
                                    <input
                                        id={field.id}
                                        name={field.name}
                                        type={field.type}
                                        placeholder={t(field.name)}
                                        className="form-control"
                                        autoComplete="email"
                                        required
                                    />
                                ) : (
                                    <div style={{ position: "relative" }}>
                                        <input
                                            id={field.id}
                                            name={field.name}
                                            type={showPassword[field.name] ? "text" : "password"}
                                            placeholder={t(field.name)}
                                            className="form-control"
                                            style={{ paddingRight: "45px" }}
                                            autoComplete="new-password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowPassword((prev) => ({
                                                    ...prev,
                                                    [field.name]: !prev[field.name],
                                                }))
                                            }
                                            style={{
                                                position: "absolute",
                                                right: "10px",
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                background: "none",
                                                border: "none",
                                                cursor: "pointer",
                                                padding: 0,
                                            }}
                                        >
                                            {showPassword[field.name] ? (
                                                <VisibilityOff style={{ color: "#64748b" }} />
                                            ) : (
                                                <Visibility style={{ color: "#64748b" }} />
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    <div className="flex items-center gap-2 mb-2">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id={'termofuse'}
                            required
                        />
                        <p className="text-white">
                            {t("confimation")}
                            <a href="/politica-de-privacidad" className="text-white underline mx-1" target="_blank" rel="noopener noreferrer">{t("privacyPolicy")}</a>
                            {t("and")}
                            <a href="/terminos-de-uso" className="text-white underline mx-1" target="_blank" rel="noopener noreferrer">{t("termOfUse")}</a>.
                        </p>
                    </div>
                    <GoogleSignUp
                        type="worker"
                        onSuccess={(user) => {
                            const hasPhone = user?.phone && user.phone.trim() !== '';
                            const isEssentialCompleted = user?.isEssentialCompleted === true;
                            const needsEssentialInfo = !isEssentialCompleted || !hasPhone;

                            if (needsEssentialInfo) {
                                navigate("/complete-essential-info", {
                                    state: {
                                        userType: "worker"
                                    }
                                });
                            } else {
                                if (onSuccess) {
                                    onSuccess();
                                }
                            }
                        }}
                    />
                    <button
                        className="theme-btn btn-style-one"
                        type="submit"
                        disabled={isLoading}
                        style={{ backgroundColor: "black" }}
                    >
                        {isLoading ? (
                            <div
                                className="spinner-border"
                                style={{ height: "15px", width: "15px" }}
                                role="status"
                            ></div>
                        ) : (
                            t('register')
                        )}
                    </button>
                </div>
            </form>
        </>
    )
}
export default RegisterWorkerForm
